import React, { PropsWithChildren, useContext, useEffect, useMemo } from 'react';
import { useCRMContext } from './CRMContext';
import { useLoadedData, useRequestLoad } from '../../hooks/useLoadedData';
import { useAutoSuggestionsStore } from '../primitives/AutoSuggests';

export interface GeoOptions {
  countries: string[];
  cities: string[];
}

export interface ICRMGeoContext {
  countries: string[];
  cities: string[];
  ensureLoaded: () => void;
  reload: () => void;
}

const CRMGeoContext = React.createContext<ICRMGeoContext>({
  countries: [],
  cities: [],
  ensureLoaded: () => {},
  reload: () => {},
});

export const useCRMGeoContext = () => useContext(CRMGeoContext);

export const useEnsureGeoLoaded = (condition?: boolean) => {
  const geoContext = useCRMGeoContext();
  useEffect(() => {
    if(condition === undefined || condition) {
      geoContext.ensureLoaded();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition]);
}

interface Props {}

export const CRMGeoSuggestionsKeys = {
  countries: "tl_crm_geo_countries",
  cities: "tl_crm_geo_cities",
}

export const CRMGeoContextProvider = (props: PropsWithChildren<Props>) => {
  const { children } = props;

  const config = useCRMContext();

  const ensureLoaded = useRequestLoad();
  
  const initialGeoOptions: GeoOptions = { countries: [], cities: [] };
  
  const geoData = useLoadedData<GeoOptions>(
    `${config.apiPath}/contact/geo-options`, 
    initialGeoOptions, 
    ensureLoaded.isLoadRequested
  );

  const autoSuggestionsStore = useAutoSuggestionsStore();

  const contextValue: ICRMGeoContext = useMemo(() => {
    autoSuggestionsStore.setOptions(CRMGeoSuggestionsKeys.countries, geoData.data.countries);
    autoSuggestionsStore.setOptions(CRMGeoSuggestionsKeys.cities, geoData.data.cities);
    
    return {
      countries: geoData.data.countries || [],
      cities: geoData.data.cities || [],
      ensureLoaded: ensureLoaded.requestLoad,
      reload: () => geoData.reload(),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoData.data, config.apiPath]);

  return (
    <CRMGeoContext.Provider value={contextValue}>
      {children}
    </CRMGeoContext.Provider>
  );
}
